import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import { logAdminActivity } from '../../utils/activityLogger';
import {
  formatRequisitionCurrency,
  formatRequisitionDate,
  getRequisitionStatusTone,
  normalizeRequisitionStatus,
  toDateValue
} from './requisitionHelpers';

const actionOptions = ['Approved', 'Partially Approved', 'Review', 'Rejected', 'On Hold'];

const normalizeAdminType = (value) => {
  const normalized = String(value || 'admin').trim().toLowerCase();
  if (normalized === 'superadmin') return 'superAdmin';
  if (normalized === 'viewer') return 'viewer';
  return 'admin';
};

const resolveApproverProfile = async (currentUser) => {
  if (!currentUser) {
    return {
      approverName: 'Finance',
      approverEmail: '',
      approverId: '',
      approverRole: 'admin'
    };
  }

  let approverName = currentUser.displayName || currentUser.email || 'Finance';
  let approverRole = 'admin';

  try {
    const byUserId = await getDocs(
      query(collection(db, 'Admins'), where('userID', '==', currentUser.uid))
    );

    if (!byUserId.empty) {
      const adminData = byUserId.docs[0].data();
      approverName = adminData.userName || adminData.userEmail || approverName;
      approverRole = normalizeAdminType(adminData.adminType);
    } else if (currentUser.email) {
      const byEmail = await getDocs(
        query(collection(db, 'Admins'), where('userEmail', '==', currentUser.email))
      );

      if (!byEmail.empty) {
        const adminData = byEmail.docs[0].data();
        approverName = adminData.userName || adminData.userEmail || approverName;
        approverRole = normalizeAdminType(adminData.adminType);
      }
    }
  } catch (error) {
    console.error('Failed to resolve approver profile:', error);
  }

  return {
    approverName,
    approverEmail: currentUser.email || '',
    approverId: currentUser.uid,
    approverRole
  };
};

function RequisitionApprovalsComp() {
  const auth = getAuth();
  const [requiLists, setRequiLists] = useState([]);
  const [branchMapping, setBranchMapping] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [approverProfile, setApproverProfile] = useState({
    approverName: 'Finance',
    approverEmail: '',
    approverId: '',
    approverRole: 'admin'
  });
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [actionModalShow, setActionModalShow] = useState(false);
  const [action, setAction] = useState('Approved');
  const [comment, setComment] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchApproverProfile = async () => {
      const profile = await resolveApproverProfile(currentUser);
      setApproverProfile(profile);
    };

    fetchApproverProfile();
  }, [currentUser]);

  useEffect(() => {
    const fetchBranches = async () => {
      const branchSnapshot = await getDocs(collection(db, 'Branches'));
      const mapping = {};

      branchSnapshot.forEach((branchDoc) => {
        mapping[branchDoc.id] = branchDoc.data().branchName;
      });

      setBranchMapping(mapping);
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchRequisitions = async () => {
      const querySnapshot = await getDocs(collection(db, 'Requisitions'));
      const nextRequisitions = querySnapshot.docs
        .map((itemDoc) => ({
          id: itemDoc.id,
          ...itemDoc.data(),
          status: normalizeRequisitionStatus(itemDoc.data().status)
        }))
        .sort((a, b) => {
          const aDate = toDateValue(a.timeStamp)?.getTime() || 0;
          const bDate = toDateValue(b.timeStamp)?.getTime() || 0;
          return bDate - aDate;
        });

      setRequiLists(nextRequisitions);
    };

    fetchRequisitions();
  }, []);

  const summary = useMemo(() => {
    const counts = {
      pending: 0,
      approved: 0,
      hold: 0,
      rejected: 0
    };

    requiLists.forEach((item) => {
      const tone = getRequisitionStatusTone(item.status);

      if (tone === 'pending') {
        counts.pending += 1;
      } else if (tone === 'approved' || tone === 'partial') {
        counts.approved += 1;
      } else if (tone === 'hold') {
        counts.hold += 1;
      } else if (tone === 'rejected') {
        counts.rejected += 1;
      } else if (tone === 'review') {
        counts.hold += 1;
      }
    });

    return counts;
  }, [requiLists]);

  const filteredRequisitions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requiLists.filter((item) => {
      const matchesStatus = statusFilter === 'All' || normalizeRequisitionStatus(item.status) === statusFilter;
      const matchesSearch =
        !normalizedSearch ||
        [
          item.itemTitle,
          item.itemDesc,
          item.requesterName,
          item.requesterEmail,
          branchMapping[item.selectedCampus]
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      return matchesStatus && matchesSearch;
    });
  }, [branchMapping, requiLists, searchTerm, statusFilter]);

  const isSuperAdmin = approverProfile.approverRole === 'superAdmin';

  const openActionModal = (item) => {
    if (!isSuperAdmin) {
      swal('Restricted', 'Only superAdmin users can approve requisitions.', 'warning');
      return;
    }

    setSelectedRequisition(item);
    setAction(normalizeRequisitionStatus(item.status) === 'Pending Approval' ? 'Approved' : normalizeRequisitionStatus(item.status));
    setComment(item.approvalComment || item.comment || '');
    setActionModalShow(true);
  };

  const handleActionSubmit = async () => {
    if (!isSuperAdmin) {
      swal('Restricted', 'Only superAdmin users can approve requisitions.', 'warning');
      return;
    }

    if (!selectedRequisition) {
      return;
    }

    if (!action) {
      swal('Missing action', 'Please choose an approval action first.', 'warning');
      return;
    }

    setIsSubmittingAction(true);

    try {
      const normalizedAction = normalizeRequisitionStatus(action);

      await setDoc(
        doc(db, 'Requisitions', selectedRequisition.id),
        {
          status: normalizedAction,
          approvalComment: comment.trim(),
          comment: comment.trim(),
          approvalDate: serverTimestamp(),
          approvedById: approverProfile.approverId,
          approvedByName: approverProfile.approverName,
          approvedByEmail: approverProfile.approverEmail
        },
        { merge: true }
      );

      setRequiLists((prev) =>
        prev.map((item) =>
          item.id === selectedRequisition.id
            ? {
                ...item,
                status: normalizedAction,
                approvalComment: comment.trim(),
                comment: comment.trim(),
                approvalDate: new Date(),
                approvedById: approverProfile.approverId,
                approvedByName: approverProfile.approverName,
                approvedByEmail: approverProfile.approverEmail
              }
            : item
        )
      );

      logAdminActivity({
        action: 'Updated requisition approval',
        details: `${normalizedAction} requisition for ${selectedRequisition.itemTitle}.`,
        meta: {
          targetCollection: 'Requisitions',
          targetId: selectedRequisition.id
        }
      });

      swal('Success!', 'Requisition status updated successfully.', 'success');
      setStatusFilter('All');
      setActionModalShow(false);
      setSelectedRequisition(null);
      setComment('');
    } catch (error) {
      console.error('Failed to update requisition status:', error);
      swal('Error!', 'The requisition could not be updated right now.', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  return (
    <div className="teamsComponent requisitionPage">
      <div className="teamsTopBar requisitionTopBar">
        <div>
          <h5 className="tHide1">Requisition Approvals</h5>
          <h5 className="tHide2">Requisition Approvals</h5>
          <p className="requisitionIntro">
            Review office requests, leave notes for the team, and decide what finance can approve.
          </p>
          {!isSuperAdmin ? (
            <p className="requisitionAccessNote">
              Approval actions are restricted to users whose admin type is set to <strong>superAdmin</strong>.
            </p>
          ) : null}
        </div>
      </div>

      <div className="requisitionSummaryGrid">
        <article className="requisitionSummaryCard">
          <span>Pending approval</span>
          <strong>{summary.pending}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Approved / partial</span>
          <strong>{summary.approved}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>On hold</span>
          <strong>{summary.hold}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Rejected</span>
          <strong>{summary.rejected}</strong>
        </article>
      </div>

      <div className="requisitionToolbar">
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-approval-search">Search</label>
          <input
            id="requisition-approval-search"
            type="text"
            placeholder="Item, requester, branch..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-approval-status-filter">Status</label>
          <select
            id="requisition-approval-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="All">All statuses</option>
            <option value="Pending Approval">Pending approval</option>
            <option value="Review">Review</option>
            <option value="Approved">Approved</option>
            <option value="Partially Approved">Partially approved</option>
            <option value="On Hold">On hold</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {filteredRequisitions.length > 0 ? (
        <div className="requisitionTableWrap">
          <table className="requisitionTable">
            <thead>
              <tr>
                <th>Item</th>
                <th>Branch</th>
                <th>Requester</th>
                <th>Needed by</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Finance note</th>
                <th>Receipt</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequisitions.map((item) => {
                const tone = getRequisitionStatusTone(item.status);
                const normalizedStatus = normalizeRequisitionStatus(item.status);

                return (
                  <tr key={item.id}>
                    <td>
                      <div className="requisitionTableMainCell">
                        <strong>{item.itemTitle}</strong>
                        <span>{item.itemDesc}</span>
                      </div>
                    </td>
                    <td>{branchMapping[item.selectedCampus] || 'Campus not set'}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{item.requesterName || 'Staff member'}</strong>
                        <span>{formatRequisitionDate(item.timeStamp)}</span>
                      </div>
                    </td>
                    <td>{formatRequisitionDate(item.itemsNeededBy)}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{formatRequisitionCurrency(item.totalAmount)}</strong>
                        <span>
                          {item.numberOfUnits} unit{Number(item.numberOfUnits) === 1 ? '' : 's'} at {formatRequisitionCurrency(item.amountPerUnit)} each
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`requisitionStatusPill requisitionStatusPill--${tone}`}>
                        {normalizedStatus}
                      </span>
                    </td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{item.approvedByName || item.approvedByEmail || 'Not reviewed yet'}</strong>
                        <span>{item.approvalComment || 'No finance note yet'}</span>
                      </div>
                    </td>
                    <td>
                      {item.receiptUrl ? (
                        <a
                          className="requisitionGhostBtn requisitionGhostBtn--table"
                          href={item.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FontAwesomeIcon icon={faReceipt} /> Receipt
                        </a>
                      ) : (
                        <span className="requisitionTableMuted">No receipt</span>
                      )}
                    </td>
                    <td>
                      {normalizedStatus !== 'Approved' && isSuperAdmin ? (
                        <button
                          type="button"
                          className="requisitionQuickBtn requisitionQuickBtn--approve requisitionQuickBtn--table"
                          onClick={() => openActionModal(item)}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} /> Review
                        </button>
                      ) : !isSuperAdmin ? (
                        <span className="requisitionTableMuted">superAdmin only</span>
                      ) : (
                        <span className="requisitionTableMuted">Approved</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="requisitionEmptyState">
          <h6>No requisitions match this filter</h6>
          <p>Try another status or search term to see more requests.</p>
        </div>
      )}

      <ApprovalActionModal
        show={actionModalShow}
        onHide={() => {
          if (!isSubmittingAction) {
            setActionModalShow(false);
          }
        }}
        onSubmit={handleActionSubmit}
        action={action}
        setAction={setAction}
        comment={comment}
        setComment={setComment}
        selectedRequisition={selectedRequisition}
        isSubmitting={isSubmittingAction}
      />
    </div>
  );
}

function ApprovalActionModal({
  show,
  onHide,
  onSubmit,
  action,
  setAction,
  comment,
  setComment,
  selectedRequisition,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Review requisition</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedRequisition ? (
          <div className="requisitionApprovalModalBody">
            <div className="requisitionApprovalPreview">
              <h6>{selectedRequisition.itemTitle}</h6>
              <p>{selectedRequisition.itemDesc}</p>
              <span>{formatRequisitionCurrency(selectedRequisition.totalAmount)}</span>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Select value={action} onChange={(event) => setAction(event.target.value)}>
                {actionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Finance note</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Leave a note explaining the approval decision."
              />
            </Form.Group>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Saving...</span>
            </>
          ) : (
            'Save decision'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequisitionApprovalsComp;
